import React, {useCallback, useMemo, useRef, useState} from 'react';

import {mergeTemplateColumns} from 'src/components/user-product-columns/product-columns';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';
import {TemplateType} from 'src/openapi/admin/models/TemplateType';

import Button from '@pb/kit-component-button';
import {OptionsModal} from '@pb/kit-component-modal-options';


import {type ColumnAdapter, defaultColumnAdapter} from './column-adapter';
import {useUpdateColumnOrder, useUpdateDefaultSorting} from './hooks';
import {prepareGroupsCheckboxTree} from './utils/prepareGroupsCheckboxTree';

import type {
    AutoSuggestCallbackData,
    GroupSingleOption,
    OptionGroups,
    OptionTab,
    PrepareGroupsCheckboxTree,
    PrepareSuggestionData,
} from '@pb/kit-component-modal-options';
import type {SearchTemplateColumn, UIColumnOption} from 'src/modules/advanced-search-result/typedef';
import type {SearchType} from 'src/openapi/admin/models/SearchType';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';


type Props = {
    columnAdapter?: ColumnAdapter,
    searchType: SearchType,
    activeTemplate: UserSearchTemplate,
    templates: UserSearchTemplate[],

    columns: Record<string, string>,
    columnsMap: SearchTemplateColumn[],
    items: OptionTab[],
    allOptions: GroupSingleOption[],
    optionGroups: OptionGroups,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    optionSettings: any,

    onClose: () => void,
    onDone: () => void,
    onFail: (error: string) => void,
};

const fixedColumnSortComparator =
    (a: UIColumnOption, b: UIColumnOption) => Number(b.fixed) - Number(a.fixed);

export const EditSearchColumnsModal = (props: Props) => {
    const {
        columnAdapter = defaultColumnAdapter,
        searchType,
        templates,
        activeTemplate,
        optionSettings,
        onClose,
        onDone,
        onFail,
    } = props;

    const [{columns, columnsMap, items, allOptions, optionGroups}] = useState(() => ({
        columns: props.columns,
        columnsMap: props.columnsMap,
        items: props.items,
        allOptions: props.allOptions,
        optionGroups: props.optionGroups,
    }));

    const [searchQuery, setSearchQuery] = useState('');
    const [restoredDefault, setRestoredDefault] = useState(false);
    const matchedIds = useRef<Set<string>>(new Set([]));

    const {busy, event: updateColumnsOrder} = useUpdateColumnOrder({onSuccess: onDone, onFail});
    const {event: updateDefaultSorting} = useUpdateDefaultSorting({
        onSuccess: () => undefined,
        onFail: () => undefined,
    });

    const defaultColumns = useMemo(() => templates.find(template =>
        template.searchType === searchType &&
        template.templateType === TemplateType.DEFAULT,
    )?.columns || {}, [templates, searchType]);

    const selectedDefaultOptions = useMemo(() => {
        const sortedDefaultColumns = Object.entries(columnAdapter.toDisplay(defaultColumns))
            .filter(([code]) => columns[code])
            .map(([code, order]) => ({code, order}))
            .sort((a, b) => a.order - b.order);

        return sortedDefaultColumns.map(column => ({
            label: columnsMap.find(template => template.code === columns[column.code])?.name || '',
            fixed: columnsMap.find(template => template.code === columns[column.code])?.required || false,
            pinned: false,
            optionId: columns[column.code],
        })) || [];
    }, [defaultColumns, columnsMap, columns, columnAdapter]);

    const [selectedOptions, setSelectedOptions] = useState<UIColumnOption[]>(
        () => Object.entries(columnAdapter.toDisplay(activeTemplate.columns))
            .filter(([code]) => columns[code])
            .map(([code, order]) => ({code, order}))
            .sort((a, b) => a.order - b.order)
            .map(column => ({
                label: columnsMap.find(template => template.code === columns[column.code])?.name || '',
                fixed: columnsMap.find(template => template.code === columns[column.code])?.required || false,
                pinned: false,
                optionId: columns[column.code],
            })) || [],
    );

    const prepareSuggestionData = useCallback<PrepareSuggestionData>(({data, query}) => {
        setSearchQuery(query);
        return data.reduce<AutoSuggestCallbackData[]>((acc, {option: {label, optionId}}) => {
            const currentLabel = label.toLowerCase();
            const currentQuery = query.trim().toLowerCase();
            const existLabel = currentLabel.includes(currentQuery);
            return [...acc, ...(existLabel ? [{id: optionId, name: label}] : [])];
        }, []);
    }, [setSearchQuery]);

    const clearMatchedIds = useCallback(() => {
        matchedIds.current.clear();
    }, [matchedIds]);

    const setMatchedId = useCallback((id: string) => {
        matchedIds.current.add(id);
    }, [matchedIds]);

    const getMatchedIds = useCallback(() => {
        return matchedIds.current;
    }, [matchedIds]);

    const saveColumns = useCallback(async () => {
        const saveMap = Object.fromEntries(Object.entries(columns).map(([code, optionId]) => [optionId, code]));
        const updatedTemplate = {
            ...activeTemplate,
            columns: columnAdapter.toStored(mergeTemplateColumns(
                [...selectedOptions].sort(fixedColumnSortComparator).map(option => saveMap[option.optionId]),
                columnAdapter.toDisplay(activeTemplate.columns),
                Object.keys(columns),
                restoredDefault,
            ), restoredDefault ? defaultColumns : activeTemplate.columns),
        };

        let sortUpdateError;
        if (!(activeTemplate.defaultSortColumn in updatedTemplate.columns)) {
            sortUpdateError = await updateDefaultSorting({
                templateId: activeTemplate.templateId,
                sortColumn: saveMap[[...selectedOptions].sort(fixedColumnSortComparator)[0].optionId],
                sortDirection: SortDirection.ASC,
            });
        }

        if (sortUpdateError) {
            onFail('Failed to update default sorting');
            return;
        }

        await updateColumnsOrder(updatedTemplate);
    }, [selectedOptions, activeTemplate, columns, restoredDefault, defaultColumns, columnAdapter,
        onFail, updateColumnsOrder, updateDefaultSorting]);

    const saveButton = useCallback(() => (
        <Button variant="Type.PRIMARY" busy={busy} disabled={!selectedOptions.length} onClick={saveColumns}>
            Save
        </Button>
    ), [selectedOptions, saveColumns, busy]);

    const restoreDefaultButton = useCallback(() => (
        <Button
            disabled={busy}
            variant="Type.TERTIARY"
            onClick={() => {
                setRestoredDefault(true);
                setSelectedOptions(selectedDefaultOptions);
            }}>
            Restore Default
        </Button>
    ), [selectedDefaultOptions, busy]);

    return (
        <OptionsModal
            selectedOptions={selectedOptions}
            saveButton={saveButton}
            restoreDefaultButton={restoreDefaultButton}
            allOptionsTabs={{
                requested: false,
                searchQuery,
                count: {},
                items,
                allOptions,
            }}
            addOptions={data => setSelectedOptions(data as UIColumnOption[])}
            onCancel={onClose}
            prepareSuggestionData={prepareSuggestionData}
            clearMatchedIds={clearMatchedIds}
            getMatchedIds={getMatchedIds}
            setMatchedId={setMatchedId}

            autoScroll={false}
            enableAutoScroll={() => false}
            findOptionInGroup={() => undefined}
            hideSelectAllInGroup={() => false}
            optionGroups={optionGroups}
            optionsName="columns"
            optionsSettings={optionSettings}
            optionsCount={allOptions.length}
            prepareGroupsCheckboxTree={prepareGroupsCheckboxTree as PrepareGroupsCheckboxTree}
        />
    );
};
